"use client";

import React, { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, parseISO, isValid } from "date-fns";
import { toast } from "sonner";
import { ResponsiveModal, useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";

const MODAL_ID = MODAL_IDS.dueDate.set;

/**
 * Payload passed to the modal via the global modal stack when the `setDueDate`
 * action runs. `onSave` is supplied by the action handler and persists through
 * the entity's generic update path (DetailActionContext.onUpdateItem).
 */
export interface SetDueDateModalData {
  /** Label of the entity whose due date is being set, for the modal description. */
  title?: string;
  /** Current due date as an ISO/`yyyy-MM-dd` string, or null when unset. */
  currentDueDate?: string | null;
  /** Persists the selected date (`yyyy-MM-dd`) or null to clear it. */
  onSave: (value: string | null) => Promise<void>;
}

function isSetDueDateData(data: unknown): data is SetDueDateModalData {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { onSave?: unknown }).onSave === "function"
  );
}

function parseDueDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

export function SetDueDateModal() {
  const { isModalOpen, closeModal, getModalData, modalDataRevision } = useGlobalModalStack();
  const open = isModalOpen(MODAL_ID);

  const data = useMemo(() => {
    const raw = getModalData(MODAL_ID);
    return isSetDueDateData(raw) ? raw : undefined;
    // modalDataRevision bumps whenever the stored payload changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getModalData, modalDataRevision, open]);

  const [selected, setSelected] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected(parseDueDate(data?.currentDueDate));
  }, [open, data?.currentDueDate]);

  const persist = async (value: string | null) => {
    if (!data) return;
    setIsSaving(true);
    try {
      await data.onSave(value);
      closeModal(MODAL_ID);
    } catch {
      toast.error("Failed to update due date");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelect = (date?: Date) => {
    if (!date || !isValid(date)) return;
    setSelected(date);
    void persist(format(date, "yyyy-MM-dd"));
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_ID}
      title="Adjust due date"
      description={data?.title ? `Set the due date for ${data.title}` : "Pick a new due date"}
      maxWidth="sm"
    >
      <div className="flex flex-col items-center gap-3 p-2">
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={isSaving}
          initialFocus
        />
        <div className="flex w-full items-center justify-between gap-2 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSaving}
            onClick={() => void persist(null)}
          >
            Clear due date
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isSaving}
            onClick={() => closeModal(MODAL_ID)}
          >
            Cancel
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default SetDueDateModal;
